import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * ScrollManager - Comprehensive scroll behavior management
 * 
 * Handles:
 * - Route changes (scrolls to top)
 * - Hash links (scrolls to anchor element)
 * - Programmatic navigation (push/replace)
 * - Browser back/forward (respects scroll restoration)
 */
const ScrollManager = () => {
  const { pathname, hash, key } = useLocation();
  const navigationType = useNavigationType();
  const lastKey = useRef(key);

  useEffect(() => {
    // Skip if same location key (prevents scroll on same-page re-renders)
    if (lastKey.current === key) return;
    lastKey.current = key;

    // Handle hash links - scroll to anchor element
    if (hash) {
      // Small delay to ensure DOM is ready
      const scrollToHash = () => {
        const element = document.getElementById(hash.slice(1));
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
          // If element not found, scroll to top
          window.scrollTo({ top: 0, behavior: "instant" });
        }
      };
      
      // Use requestAnimationFrame for better timing
      requestAnimationFrame(() => {
        requestAnimationFrame(scrollToHash);
      });
      return;
    }

    // For POP (back/forward), let browser handle scroll restoration
    if (navigationType === "POP") {
      // Browser handles scroll restoration for back/forward
      return;
    }

    // For PUSH and REPLACE navigation, scroll to top
    window.scrollTo({ top: 0, behavior: "instant" });

  }, [pathname, hash, key, navigationType]);

  return null;
};

export default ScrollManager;
